import { useEffect, useState } from "react";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  DialogTitle,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CustomButton, CustomLoadingButton } from "src/design";
import {
  BILLING_PERIODS,
  BillingPeriod,
  monthsIn,
} from "src/business/payment/billingPeriod";
import { useConfirmPayment } from "src/data/admin/queries/payment";
import {
  AdminPaymentDto,
  ConfirmPaymentRequest,
} from "src/data/admin/types/payment";

interface ConfirmPaymentDialogProps {
  /** The payment to confirm; `null` keeps the dialog closed. */
  payment: AdminPaymentDto | null;
  /** Tier names from the public catalog. */
  tiers: string[];
  onClose: () => void;
  onConfirmed: (payment: AdminPaymentDto) => void;
  onError: (message: string) => void;
}

/**
 * Confirming applies the payment to the subscription — extending it for a
 * renewal, granting the target tier from today for an upgrade — and there is no
 * step after this one, so the dialog says so outright.
 *
 * The fields are prefilled from the declaration and diffed on submit: only what
 * the admin actually changed is sent. An untouched dialog posts `{}`, which the
 * backend reads as "keep every declared value" — echoing them back would freeze
 * them if it ever started recomputing a field at confirmation.
 */
export const ConfirmPaymentDialog = ({
  payment,
  tiers,
  onClose,
  onConfirmed,
  onError,
}: ConfirmPaymentDialogProps) => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<BillingPeriod>("MONTHLY");
  const [months, setMonths] = useState("1");
  const [targetTier, setTargetTier] = useState("");
  const [amount, setAmount] = useState("0");

  useEffect(() => {
    if (!payment) return;
    setPeriod(payment.period);
    setMonths(String(payment.months));
    setTargetTier(payment.targetTier);
    setAmount(String(payment.amount));
  }, [payment]);

  // The backend resets the months to the period's length when a period is
  // supplied, so the field follows the picker here rather than silently
  // disagreeing with what confirmation will store. Ops can still overtype it.
  const handlePeriodChange = (next: BillingPeriod) => {
    setPeriod(next);
    setMonths(String(monthsIn(next)));
  };

  const confirmPayment = useConfirmPayment({
    onSuccess: (confirmed) => {
      onConfirmed(confirmed);
      onClose();
    },
  });

  const parsedMonths = Number(months);
  const parsedAmount = Number(amount);
  const isValid =
    Number.isFinite(parsedMonths) &&
    parsedMonths >= 1 &&
    Number.isFinite(parsedAmount) &&
    parsedAmount >= 0 &&
    !!targetTier;

  // The catalog may not carry a legacy tier the payment still points at.
  const tierOptions = tiers.includes(payment?.targetTier ?? "")
    ? tiers
    : [...tiers, payment?.targetTier ?? ""].filter(Boolean);

  const handleSubmit = () => {
    if (!payment || !isValid) return;

    const overrides: ConfirmPaymentRequest = {};
    if (period !== payment.period) overrides.period = period;
    if (parsedMonths !== payment.months) overrides.months = parsedMonths;
    if (targetTier !== payment.targetTier) overrides.targetTier = targetTier;
    if (parsedAmount !== payment.amount) overrides.amount = parsedAmount;

    confirmPayment.mutate(
      { paymentId: payment.id, overrides },
      {
        onError: (error) =>
          onError(
            error instanceof Error
              ? error.message
              : t("payments.admin.confirm.failed"),
          ),
      },
    );
  };

  return (
    <Dialog
      open={!!payment}
      onClose={() => !confirmPayment.isPending && onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {t("payments.admin.confirm.title", {
          restaurant: payment?.restaurantName ?? "",
        })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t("payments.admin.confirm.body")}
        </DialogContentText>

        <Alert severity="warning" sx={{ mb: 2 }}>
          {payment?.type === "UPGRADE"
            ? t("payments.admin.confirm.upgradeConsequence")
            : t("payments.admin.confirm.renewalConsequence")}
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("payments.admin.confirm.period")}</InputLabel>
              <Select
                label={t("payments.admin.confirm.period")}
                value={period}
                onChange={(event) =>
                  handlePeriodChange(event.target.value as BillingPeriod)
                }
              >
                {BILLING_PERIODS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {t(`payments.period.${option}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={t("payments.admin.confirm.months")}
              value={months}
              onChange={(event) => setMonths(event.target.value)}
              inputProps={{ min: 1 }}
              error={!Number.isFinite(parsedMonths) || parsedMonths < 1}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("payments.admin.confirm.tier")}</InputLabel>
              <Select
                label={t("payments.admin.confirm.tier")}
                value={targetTier}
                onChange={(event) => setTargetTier(event.target.value)}
              >
                {tierOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={t("payments.admin.confirm.amount")}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              inputProps={{ min: 0 }}
              error={!Number.isFinite(parsedAmount) || parsedAmount < 0}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <CustomButton
          color="inverted"
          onClick={onClose}
          disabled={confirmPayment.isPending}
        >
          {t("payments.admin.confirm.cancel")}
        </CustomButton>
        <CustomLoadingButton
          loading={confirmPayment.isPending}
          disabled={!isValid || confirmPayment.isPending}
          onSubmit={handleSubmit}
          label={t("payments.admin.confirm.submit")}
          fullwidth={false}
        />
      </DialogActions>
    </Dialog>
  );
};
